/**
 * Nhãn trạng thái thiết bị theo Bảng 3.4, và nhãn thời điểm kèm tuổi dữ liệu.
 *
 * Màu KHÔNG phải phương tiện duy nhất: mỗi nhãn luôn kèm CHỮ và một biểu tượng có
 * HÌNH DẠNG riêng (tam giác, tròn, vuông, gạch, chấm hỏi), để người dùng khó phân
 * biệt màu vẫn đọc được trạng thái.
 */
import type { ReactNode } from 'react';
import { vi } from '../ngon-ngu/vi';
import type { MaTrangThaiThietBi } from '../api/thiet-bi-api';

const HINH_DANG: Record<MaTrangThaiThietBi, string> = {
  dang_phat: '▶',
  cho_phat: '●',
  tam_dung: '❚❚',
  mat_ket_noi: '▲',
  chua_kich_hoat: '○',
};

export function NhanTrangThai({ ma }: { ma: MaTrangThaiThietBi }): ReactNode {
  const nhan = vi.trangThaiThietBi[ma];
  return (
    <span className={`nhan-tt nhan-tt--${ma.replace(/_/g, '-')}`} data-trang-thai={ma}>
      <span className="nhan-tt__hinh" aria-hidden="true">
        {HINH_DANG[ma]}
      </span>
      <span className="nhan-tt__chu">{nhan}</span>
    </span>
  );
}

/** Tuổi dữ liệu dạng người đọc được: "12 giây", "1 giờ 13 phút". */
export function tuoiThanhChu(giay: number | null): string {
  if (giay === null) return vi.tuoiDuLieu.chuaCo;
  if (giay < 60) return `${giay} ${vi.tuoiDuLieu.giay}`;
  if (giay < 3600) return `${Math.floor(giay / 60)} ${vi.tuoiDuLieu.phut}`;
  if (giay < 86_400) {
    const gio = Math.floor(giay / 3600);
    const phut = Math.floor((giay % 3600) / 60);
    return `${gio} ${vi.tuoiDuLieu.gio}${phut > 0 ? ` ${phut} ${vi.tuoiDuLieu.phut}` : ''}`;
  }
  return `${Math.floor(giay / 86_400)} ${vi.tuoiDuLieu.ngay}`;
}

export function gioThanhChu(epochGiay: number | null): string {
  if (epochGiay === null) return '—';
  const d = new Date(epochGiay * 1000);
  const hai = (n: number): string => String(n).padStart(2, '0');
  return `${hai(d.getHours())}:${hai(d.getMinutes())}:${hai(d.getSeconds())}`;
}

/**
 * Thời điểm cập nhật LUÔN kèm tuổi dữ liệu trong ngoặc — yêu cầu bắt buộc của
 * mục 3.1 và 3.5: người dùng phải biết số đang xem cũ bao nhiêu.
 */
export function NhanThoiDiem({
  epochGiay,
  tuoiGiay,
}: {
  epochGiay: number | null;
  tuoiGiay: number | null;
}): ReactNode {
  return (
    <span className="nhan-thoi-diem">
      <time dateTime={epochGiay ? new Date(epochGiay * 1000).toISOString() : undefined}>
        {gioThanhChu(epochGiay)}
      </time>{' '}
      <span className="nhan-thoi-diem__tuoi" title={vi.tuoiDuLieu.nhan}>
        ({tuoiThanhChu(tuoiGiay)})
      </span>
    </span>
  );
}
